import {
  FunctionComponent,
  ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';

import {
  Job,
  PaymentOption,
  shopStripePromise,
  stripePromise as standardStripePromise,
} from '@kanda-libs/ks-frontend-services';
import { themeConfig } from '@kanda-libs/ks-tailwind-config';

import {
  Appearance,
  CssFontSource,
  CustomFontSource,
  Stripe,
} from '@stripe/stripe-js';
import { SHOP_CIDS } from 'config';
import { getLatestCardPaymentFromJob } from 'utils';

export interface CardPaymentFormContextProps {
  job?: Job;
  children: ReactNode;
  title?: string;
  showActionRequired?: boolean;
  buttonLabel?: string;
  showMobileTitle?: boolean;
  customHeading?: ReactNode;
}

export interface CardPaymentFormContextType {
  isLoading: boolean;
  jobId?: string;
  paymentOption?: PaymentOption;
  clientSecret?: string;
  stripeAppearance?: Appearance;
  stripeFont?: Array<CssFontSource | CustomFontSource>;
  stripePromise: PromiseLike<Stripe | null> | Stripe | null;
  title: string;
  showActionRequired: boolean;
  buttonLabel: string;
  showMobileTitle: boolean;
  customHeading: ReactNode;
}

export const CardPaymentFormContext = createContext<CardPaymentFormContextType>(
  {
    isLoading: false,
    jobId: undefined,
    paymentOption: undefined,
    clientSecret: undefined,
    stripeAppearance: undefined,
    stripeFont: undefined,
    stripePromise: null,
    title: 'Pay down payment by card',
    showActionRequired: true,
    buttonLabel: 'Pay deposit',
    showMobileTitle: false,
    customHeading: null,
  },
);

export const useCardPaymentFormContext = () =>
  useContext(CardPaymentFormContext);

const CardPaymentFormProvider: FunctionComponent<CardPaymentFormContextProps> =
  function ({
    job,
    title = 'Pay deposit by card',
    showActionRequired = true,
    buttonLabel = 'Pay deposit',
    showMobileTitle = false,
    customHeading = null,
    children,
  }) {
    const isLoading = useMemo(() => !job, [job]);

    const payment = useMemo(() => getLatestCardPaymentFromJob(job), [job]);

    const jobId = useMemo(() => job?.id, [job]);
    const paymentOption = useMemo(() => payment?.payment_option, [payment]);
    const clientSecret = useMemo(() => payment?.xref, [payment]);

    const { theme } = themeConfig;
    const fontFamilyRaw = theme?.extend?.fontFamily?.sans?.[0];
    const fontFamily = `${fontFamilyRaw?.replace(/"/g, '')}, sans-serif`;

    const stripeAppearance = useMemo(() => {
      if (!fontFamily) return undefined;
      return {
        theme: 'stripe' as Appearance['theme'],
        variables: {
          fontFamily,
          spacingUnit: theme.spacing[1],
          colorBackground: theme.colors['neutral-100'],
          colorText: theme.colors['neutral-700'],
          marginBottom: '16px',
          spacingGridRow: '16px',
          borderRadius: '8px',
          borderColor: theme.colors['neutral-700'],
        },
        rules: {
          '.Label': {
            fontWeight: 'bold',
            marginBottom: theme.spacing[3],
            fontSize: '12px',
          },
        },
      };
    }, [fontFamily, theme]);

    const stripeFont = useMemo(() => {
      if (!fontFamilyRaw) return undefined;
      return [
        {
          family: fontFamilyRaw,
          src: 'url(https://storage.googleapis.com/kanda-fonts/GalanoGrotesqueRegular.ttf)',
          weight: '400',
        },
        {
          family: fontFamilyRaw,
          src: 'url(https://storage.googleapis.com/kanda-fonts/GalanoGrotesqueBold.ttf)',
          weight: '600',
        },
      ];
    }, [fontFamilyRaw]);

    const stripePromise = useMemo(() => {
      if (!job) return null;
      const { cid } = job;
      if (!cid) return standardStripePromise;
      if (SHOP_CIDS.includes(cid)) return shopStripePromise;
      return standardStripePromise;
    }, [job]);

    const values = useMemo(
      () => ({
        isLoading,
        jobId,
        paymentOption,
        clientSecret,
        stripeAppearance,
        stripeFont,
        stripePromise,
        title,
        showActionRequired,
        buttonLabel,
        showMobileTitle,
        customHeading,
      }),
      [
        isLoading,
        jobId,
        paymentOption,
        clientSecret,
        stripeAppearance,
        stripeFont,
        title,
        showActionRequired,
        buttonLabel,
        showMobileTitle,
        stripePromise,
        customHeading,
      ],
    );

    return (
      <CardPaymentFormContext.Provider value={values}>
        {children}
      </CardPaymentFormContext.Provider>
    );
  };

export default CardPaymentFormProvider;
